import React from 'react';
import rest from '@data/restWrapper';
import { setModalContent, closeModal } from '@components/modal';
import ConfirmButtons from '@components/buttons/confirmButtons';
import AssignPriceList from './AssignPriceList';
import numbro from 'numbro';
import { checkOfferMismatch } from '../utils';
import { ReservationController } from '../resController';
import { translate } from '@data/translations';
import dayjs from 'dayjs';
import { PriceOffer, ReservationStatus } from '@common/modelDefinition';
import { ReactComponent as InfoIcon } from '../../../assets/custom/icons/info.svg';
import { ReactComponent as CustomerIcon } from '../../../assets/custom/icons/user.svg';
import { ReactComponent as CustomersIcon } from '../../../assets/custom/icons/users.svg';
import { ReactComponent as CalendarIcon } from '../../../assets/custom/icons/calendar.svg';
import useStore from '@data/state/zustand';
import { stopGlobalLoading } from '@components/loaders/globalLoading';
import { toast } from 'react-toastify';

import { 
    parseGuestTaxConfig, 
    hasChildrenWithoutAge, 
} from '../utils';

const ctrl = new ReservationController();

// ============================================================================
// MAIN COMPONENT
// ============================================================================

function AccommodationPriceOffer(props: any) {
    const { reservation } = props;
   
    const [offerInvoiceItems, setOfferInvoiceItems] = React.useState([]);
    const [proformaInvoiceItems, setProformaInvoiceItems] = React.useState([]);

    const [priceOfferIntervals, setPriceOfferIntervals] = React.useState([]);
    const [, setProformaPriceOfferIntervals] = React.useState([]);

    const defaultCurrency: string = useStore.getState().defaultCurrency;

    const {
        id,
        accommodationPriceListId,
        checkOutTimestamp,
        checkInTimestamp,
        adultsNumber,
        childrenNumber,
        genuineInvoiceIssued,
        openPrice,
        statusEnum,
        canceledReservationInvoicingStatus,
        guestTaxConfig,
        board,
    } = reservation;



    React.useEffect(() => {
    const loadProformaOffer = async () => {
        const reservationOfferArr = reservation.rememberedAccommodationPriceInfo
            ? reservation.rememberedAccommodationPriceInfo
            : null;
        const reservationOffer =
            reservationOfferArr && reservationOfferArr.length > 0
                ? reservationOfferArr[reservationOfferArr.length - 1]
                : reservation;
        
        const offerWithGuestConfig = {
            ...reservationOffer,
            guestTaxConfig: reservationOffer.guestTaxConfig ?? reservation.guestTaxConfig,
            board: reservationOffer.board ?? reservation.board,
            guestDates: reservationOffer.guestDates,
        };
        
        const { invoiceItems: proformaItems, priceOfferByLinkedIntervals: proformaOffer } = await rest(
            '/getAccommodationOffer',
            'POST',
            { reservation: offerWithGuestConfig }
        );
        
        setProformaInvoiceItems(proformaItems);
        setProformaPriceOfferIntervals(proformaOffer);
    };
    
    const loadCurrentOffer = async () => {
        const { invoiceItems: items, priceOfferByLinkedIntervals: offer } = await rest(
            '/getAccommodationOffer',
            'POST',
            { reservation: reservation }
        );
        
        setOfferInvoiceItems(items);
        setPriceOfferIntervals(offer);
    };
    
    if (accommodationPriceListId || openPrice || statusEnum === 3) {
        loadProformaOffer().catch((err) => {
            console.warn(err);
        });

        loadCurrentOffer().catch((err) => {
            console.warn(err);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
}, [
    id,
    accommodationPriceListId,
    checkInTimestamp,
    checkOutTimestamp,
    adultsNumber,
    childrenNumber,
    reservation.rememberedAccommodationPriceInfo,
    openPrice,
    statusEnum,
    canceledReservationInvoicingStatus,
    guestTaxConfig,
    board,
]);

    if (!accommodationPriceListId && !openPrice && canceledReservationInvoicingStatus !== 2) {
        return <AssignPriceList {...props} />;
    }



    const mismatchOffer = checkOfferMismatch({
        invoiceItems: offerInvoiceItems,
        proformaInvoiceItems,
        reservations: [reservation],
    });

    const hasMissmatch = mismatchOffer?.length > 0 && !genuineInvoiceIssued;

    const totalToPay = proformaInvoiceItems
        .map((i: any) => i.bruttoValueWithDiscount)
        .reduce((a: number, b: number) => a + b, 0);
    const totalTax = proformaInvoiceItems.map((i: any) => i.totalTaxValue).reduce((a: number, b: number) => a + b, 0);

    const totalToPayChangeDetected = offerInvoiceItems
        .map((i: any) => i.bruttoValueWithDiscount)
        .reduce((a: number, b: number) => a + b, 0);

    const totalTaxChangeDetected = offerInvoiceItems
        .map((i: any) => i.totalTaxValue)
        .reduce((a: number, b: number) => a + b, 0);

    const childrenNeedAge = hasChildrenWithoutAge(reservation);

    const preview = (
        <div className="mt-4p border-top">
            <div className="display-flex mobile-page-form">
                <div className="flex border-right pr-4p">
                    <div className="text-bold mb-4p mt-4p display-flex align-items-center justify-content-between">
                        <div className="display-flex align-items-center">
                            {reservation && (reservation.accommodationPriceListId || reservation.openPrice) ? (
                                <button
                                    disabled={
                                        reservation.genuineInvoiceIssued === 1 ||
                                        reservation?.statusEnum === ReservationStatus.closed
                                            ? true
                                            : false
                                    }
                                    onClick={(e) => {
                                        e.preventDefault();
                                        const _res = { ...reservation };
                                        _res.openPrice = null;
                                        _res.accommodationPriceListId = null;
                                        ctrl.updateActiveReservation(_res);
                                    }}
                                    className="button-info mr-8p"
                                >
                                    <i className="fa fa-exchange" />
                                </button>
                            ) : null}
                            {translate('Given price offer')}:{' '}
                        </div>
                        <div
                            className="fill-primary pointer"
                            onClick={() => {
                                setModalContent(
                                    <div>
                                        <DisplayCompressedPriceOffer
                                            showClearPriceListButton={false}
                                            invoiceItems={offerInvoiceItems}
                                            defaultCurrency={defaultCurrency}
                                            reservation={reservation}
                                            res={reservation}
                                        />

                                        <OfferBreakdown
                                            reservation={reservation}
                                            priceOfferIntervals={priceOfferIntervals}
                                            invoiceItems={offerInvoiceItems}
                                            defaultCurrency={defaultCurrency}
                                        />
                                    </div>,
                                    translate('Accommodation info'),
                                    false,
                                    'modal-md'
                                );
                            }}
                        >
                            <InfoIcon />
                        </div>
                    </div>
                    <React.Fragment>
                        <div className={hasMissmatch ? 'color-error ' : 'color-primary '}>
                            <div className="clear display-flex align-items-center">
                                <div className="reservation-form-accommodation">
                                    {totalToPay
                                        ? numbro(totalToPay).format({
                                              mantissa: 2,
                                              trimMantissa: false,
                                              thousandSeparated: true,
                                          })
                                        : 0}{' '}
                                    {defaultCurrency}
                                    &ensp;|&ensp;
                                    <b>{translate('Tax')}:</b>{' '}
                                    {totalTax
                                        ? numbro(totalTax).format({
                                              mantissa: 2,
                                              trimMantissa: false,
                                              thousandSeparated: true,
                                          })
                                        : 0}{' '}
                                    {defaultCurrency}
                                </div>
                            </div>
                        </div>
                    </React.Fragment>
                </div>

                {hasMissmatch ? (
                    <div className="flex ml-4p ">
                        <div className="text-bold mt-4p mb-4p">{translate('Given price offer change detected')}:</div>
                        <React.Fragment>
                            <div className={'color-primary '}>
                                <div className="clear display-flex align-items-center">
                                    <div className="reservation-form-accommodation">
                                        {totalToPayChangeDetected
                                            ? numbro(totalToPayChangeDetected).format({
                                                  mantissa: 2,
                                                  trimMantissa: false,
                                                  thousandSeparated: true,
                                              })
                                            : 0}{' '}
                                        {defaultCurrency}
                                        &ensp;|&ensp;
                                        <b>{translate('Tax')}:</b>{' '}
                                        {totalTaxChangeDetected
                                            ? numbro(totalTaxChangeDetected).format({
                                                  mantissa: 2,
                                                  trimMantissa: false,
                                                  thousandSeparated: true,
                                              })
                                            : 0}{' '}
                                        {defaultCurrency}
                                    </div>
                                </div>
                            </div>
                        </React.Fragment>
                        <div className="text-center">
                            <ApplyButton
                                buttonText={translate('Apply new price')}
                                reservation={reservation}
                                invoiceItems={offerInvoiceItems}
                                priceOfferIntervals={priceOfferIntervals}
                                childrenNeedAge={childrenNeedAge}
                            />
                        </div>
                    </div>
                ) : !genuineInvoiceIssued ? (
                    <div className="flex ml-4p">
                        <UndoApplyNewOffer buttonText={translate('Undo last offer')} reservation={reservation} />
                    </div>
                ) : null}
            </div>
        </div>
    );

    return <div>{preview}</div>;
}

// ============================================================================
// DISPLAY COMPRESSED PRICE OFFER
// ============================================================================

const DisplayCompressedPriceOffer = (props: any) => {
    const { title = '', defaultCurrency, invoiceItems, res } = props;
    const { adultsNumber = 1, childrenNumber = 0 } = res;
    
    const parsedConfig = parseGuestTaxConfig(res);
    
    const effectiveAdults = parsedConfig?.adults?.length || (adultsNumber - childrenNumber) || 1;
    const effectiveChildren = parsedConfig?.children?.length || childrenNumber || 0;
    const childrenAges = parsedConfig?.children?.map((c: any) => c.age) || [];
    
    const totalToPay = invoiceItems
        .map((i: any) => i.bruttoValueWithDiscount)
        .reduce((a: number, b: number) => a + b, 0);
    const daysCount = invoiceItems.map((i: any) => i.quantity).reduce((a: number, b: number) => a + b, 0);

    return (
        <div>
            <div className="font-40p">{title} </div>

            <div className={''}>
                <div className="flex-center-between pb-12p">
                    <div className="display-flex align-items-center">
                        <div className="icon-blue">
                            <CustomerIcon />
                        </div>
                        <div className="ml-4p">
                            {effectiveAdults} {translate('Adults')}
                        </div>
                    </div>
                    <div className="display-flex align-items-center">
                        <div className="icon-blue">
                            <CustomersIcon />
                        </div>
                        <div className="ml-4p">
                            {effectiveChildren} {translate('Children')}
                            {childrenAges.length > 0 && (
                                <span style={{ fontSize: '11px', color: '#6c757d', marginLeft: '4px' }}>
                                    ({childrenAges.map((a: number) => a >= 0 ? a : '?').join(', ')} {translate('yrs')})
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="display-flex align-items-center">
                        <div className="icon-blue">
                            <CalendarIcon />
                        </div>
                        <div className="ml-4p">
                            {daysCount} {translate('Days')}
                        </div>
                    </div>
                </div>
                <div className="info-total-to-pay">
                    {totalToPay
                        ? numbro(totalToPay).format({
                              mantissa: 2,
                              trimMantissa: false,
                              thousandSeparated: true,
                          })
                        : 0}{' '}
                    <p>{defaultCurrency}</p>
                </div>
            </div>
        </div>
    );
};

// ============================================================================
// OFFER BREAKDOWN
// ============================================================================

const OfferBreakdown = (props: any) => {
    const { priceOfferIntervals, reservation, defaultCurrency } = props;

    const { adultsNumber } = reservation;
    
    const parsedConfig = parseGuestTaxConfig(reservation);
    
    const effectiveAdults = parsedConfig?.adults?.length || adultsNumber;
    const childrenAges = parsedConfig?.children?.map((c: any) => c.age).filter((a: number) => a >= 0) || [];

    return (
        <div className="mt-2p ">
            {priceOfferIntervals.map((p: any, index: number) => {
                const { apRules, childrenPriceRules, from, to } = p;
                
                const pricesPerAdult = apRules.map((price: number, idx: number) => {
                    let result = null;
                    if (effectiveAdults === idx + 1) {
                        result = (
                            <span>
                                <b>{idx + 1}</b> {translate('person(s)')} &#8594; {price} {defaultCurrency}{' '}
                                {translate('per day')}
                            </span>
                        );
                    } else {
                        result = (
                            <small>
                                {idx + 1} &#8594; {price} {defaultCurrency}
                            </small>
                        );
                    }
                    return <span key={idx}>{result}; &ensp;</span>;
                });

                const childrenPrices = childrenPriceRules && childrenAges.length > 0 ? (
                    <div className="mt-4p" style={{ fontSize: '12px', color: '#28a745' }}>
                        <i className="mr-4p" />
                        <b>{translate('Children')}:</b>{' '}
                        {childrenAges.map((age: number, idx: number) => {
                            const rule = childrenPriceRules.find(
                                (r: any) => age >= r.minAge && age <= r.maxAge
                            );
                            const childPrice = rule?.price !== undefined 
                                ? rule.price 
                                : (rule?.priceIndex !== undefined ? apRules[rule.priceIndex] : 0);
                            return (
                                <span key={idx} style={{ 
                                    backgroundColor: '#d4edda', 
                                    padding: '1px 4px', 
                                    borderRadius: '4px',
                                    marginRight: '4px',
                                }}>
                                    {age} {translate('yrs')} &#8594; {childPrice} {defaultCurrency}
                                </span>
                            );
                        })}
                    </div>
                ) : null;

                const _from = dayjs(from).format('LL');
                const _to = dayjs(to).format('LL');
                const header =
                    _from === _to ? (
                        <b>{_from}</b>
                    ) : (
                        <b>
                            {_from} &#8594; {_to}
                        </b>
                    );
                    
                return (
                    <div key={index} className={'price-details '}>
                        <div>{header}</div>
                        <div>{pricesPerAdult}</div>
                        {childrenPrices}
                    </div>
                );
            })}
        </div>
    );
};

// ============================================================================
// APPLY BUTTON
// ============================================================================

const ApplyButton = (props: any) => {
    const {  priceOfferIntervals, buttonText = translate('Apply'), childrenNeedAge = false, reservation } = props;
    return (
        <div
            onClick={(e) => {
                e.preventDefault();
                
                if (childrenNeedAge && !reservation?.openPrice) {
                    toast(translate('Please set children ages before applying new price'), {
                        type: toast.TYPE.WARNING,
                        position: toast.POSITION.TOP_RIGHT,
                    });
                }
                
                setModalContent(
                    <div className="container-fluid">
                        <div className="text-center">
                            {translate('Please confirm.')}
                            <hr />
                        </div>

                        <div className="text-center">
                            <ConfirmButtons
                                onConfirm={async () => {
                                    try {
                                        const freshReservation = ctrl.getActiveReservation();
                                        
                                        const {
                                            accommodationPriceListId,
                                            checkInTimestamp,
                                            checkOutTimestamp,
                                            adultsNumber,
                                            childrenNumber,
                                            openPrice,
                                            canceledReservationInvoicingStatus,
                                            id,
                                            board,
                                            guestTaxConfig,
                                        } = freshReservation;
                                        
                                        // Parsiraj guestTaxConfig
                                        const parsedGuestTaxConfig = guestTaxConfig
                                            ? typeof guestTaxConfig === 'string'
                                                ? JSON.parse(guestTaxConfig)
                                                : guestTaxConfig
                                            : null;
                                        
                                        // Ekstrahiraj guestDates iz VisaInfo (za prave goste)
                                        let guestDates: any[] | undefined = undefined;
                                        if (freshReservation.VisaInfo && freshReservation.VisaInfo.length > 0) {
                                            const dates = freshReservation.VisaInfo
                                                .filter((v: any) => v.checkInTimestamp || v.checkOutTimestamp)
                                                .map((v: any) => ({
                                                    id: v.id,
                                                    checkIn: v.checkInTimestamp || null,
                                                    checkOut: v.checkOutTimestamp || null,
                                                }));
                                            
                                            if (dates.length > 0) {
                                                guestDates = dates;
                                            }
                                        }
                                        
                                        const newOffer: PriceOffer = {
                                            accommodationPriceListId,
                                            checkInTimestamp,
                                            checkOutTimestamp,
                                            adultsNumber,
                                            childrenNumber,
                                            priceOfferIntervals: priceOfferIntervals,
                                            openPrice,
                                            canceledReservationInvoicingStatus,
                                            id,
                                            board,
                                            guestTaxConfig: parsedGuestTaxConfig,
                                            guestDates,
                                        };
                                        
                                        const res = { ...freshReservation };
                                        
                                        if (!res.rememberedAccommodationPriceInfo) {
                                            res.rememberedAccommodationPriceInfo = [];
                                        }
                                        
                                        const offers = [...res.rememberedAccommodationPriceInfo];
                                        offers.push(newOffer);
                                        res.rememberedAccommodationPriceInfo = offers;
                                        
                                        ctrl.updateActiveReservation(res);
                                        closeModal();
                                    } catch (error: any) {
                                        stopGlobalLoading();
                                        toast(translate(error.message), { type: toast.TYPE.ERROR });
                                        closeModal();
                                    }
                                }}
                                onCancel={() => {
                                    closeModal();
                                }}
                            />
                        </div>
                    </div>,

                    translate('Apply new offer'),
                    false,
                    'modal-md'
                );
            }}
            className="button-warning mt-8p"
            // style={childrenNeedAge && !reservation.openPrice ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
        >
            {buttonText}
        </div>
    );
};

// ============================================================================
// UNDO APPLY NEW OFFER
// ============================================================================

const UndoApplyNewOffer = (props: any) => {
    const { reservation, buttonText = translate('Undo last offer') } = props;
    if (!reservation.rememberedAccommodationPriceInfo || reservation.rememberedAccommodationPriceInfo.length < 2) {
        return null;
    }

    return (
        <div className="text-center">
            <div
                onClick={() =>
                    setModalContent(
                        <div className="container-fluid">
                            <div className="text-center">
                                {translate('Please confirm.')}
                                <hr />
                            </div>

                            <div className="text-center">
                                <ConfirmButtons
                                    onConfirm={async () => {
                                        try {
                                            const res = { ...reservation };
                                            const offers = [...res.rememberedAccommodationPriceInfo];
                                            offers.pop();
                                            res.rememberedAccommodationPriceInfo = offers;
                                            ctrl.updateActiveReservation(res);
                                            closeModal();
                                        } catch (error: any) {
                                            stopGlobalLoading();
                                            toast(translate(error.message), { type: toast.TYPE.ERROR });
                                            closeModal();
                                        }
                                    }}
                                    onCancel={() => {
                                        closeModal();
                                    }}
                                />
                            </div>
                        </div>,

                        translate('Undo last offer'),
                        false,
                        'modal-md'
                    )
                }
                className="button-light mt-8p"
            >
                {buttonText}
            </div>
        </div>
    );
};

export default AccommodationPriceOffer;